document.addEventListener('algolia.hooks.initialize', function() {
  // Global variables
  window.algoliaCollectionData = new Map();
  window.algoliaNewSearchStarted = true;
  
  // Storage keys
  const STORAGE_KEYS = {
    RECENT_SEARCHES: 'AUTOCOMPLETE_RECENT_SEARCHES',
    RECENT_PRODUCTS: 'PRODUCT_VIEW_HISTORY',
    ANALYTICS_CLICKS: 'algolia_analytics_clicked_objects'
  };

  // Utility functions
  const utils = {
    getFromStorage(key) {
      try {
        return JSON.parse(localStorage.getItem(key) || '[]');
      } catch (error) {
        console.error(`Error getting ${key} from storage:`, error);
        return [];
      }
    },

    setToStorage(key, data) {
      try {
        localStorage.setItem(key, JSON.stringify(data));
      } catch (error) {
        console.error(`Error setting ${key} to storage:`, error);
      }
    },

    highlightMatch(text, query) {
      if (!query) return text;
      const regex = new RegExp(`(${query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
      return text.replace(regex, '<strong>$1</strong>');
    },

    getMenuStructure() {
      try {
        const menuContainer = document.getElementById('HeaderMenu');
        if (!menuContainer) {
          console.error('Menu container not found');
          return [];
        }
        const menuStructure = menuContainer.getAttribute('data-menu-structure');
        if (!menuStructure) {
          console.error('data-menu-structure attribute not found');
          return [];
        }
        return JSON.parse(menuStructure);
      } catch (error) {
        console.error('Error parsing menu structure:', error);
        return [];
      }
    },

    findCollectionInMenu(menu, collectionHandle) {
      for (let item of menu) {
        if (item.url && item.url.includes(`/collections/${collectionHandle}`)) {
          return { item, path: [{ title: item.title, url: item.url, handle: item.handle }] };
        }
        if (item.links) {
          for (let child of item.links) {
            if (child.url && child.url.includes(`/collections/${collectionHandle}`)) {
              return { 
                item: child, 
                path: [
                  { title: item.title, url: item.url, handle: item.handle }, 
                  { title: child.title, url: child.url, handle: child.handle }
                ] 
              };
            }
            if (child.links) {
              for (let grandchild of child.links) {
                if (grandchild.url && grandchild.url.includes(`/collections/${collectionHandle}`)) {
                  return { 
                    item: grandchild, 
                    path: [
                      { title: item.title, url: item.url, handle: item.handle }, 
                      { title: child.title, url: child.url, handle: child.handle }, 
                      { title: grandchild.title, url: grandchild.url, handle: grandchild.handle }
                    ] 
                  };
                }
              }
            }
          }
        }
      }
      return null;
    },

    organizeCollections(collections, menuStructure) {
      const organizedCollections = {};
      const addedCollections = new Set();
      const separatorSVG = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 9.8125L7.90625 6.40625L4.5 3" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    
      function addCollection(collection, path) {
        if (addedCollections.has(collection.handle)) return;
    
        const parentKey = path.length > 1 
          ? path.slice(0, -1).map(p => p.title).join(` ${separatorSVG} `)
          : 'root';
    
        if (!organizedCollections[parentKey]) {
          organizedCollections[parentKey] = [];
        }
    
        organizedCollections[parentKey].push({
          ...collection,
          fullPath: path,
          showBreadcrumb: path.length > 1
        });
    
        addedCollections.add(collection.handle);
      }
    
      collections.forEach(collection => {
        const result = this.findCollectionInMenu(menuStructure, collection.handle);
        if (result) {
          addCollection(collection, result.path);
        }
      });
    
      // Remove parent categories that have children and empty categories
      Object.keys(organizedCollections).forEach(key => {
        if (key !== 'root' && organizedCollections[key].length > 0) {
          const parentCollection = organizedCollections[key].find(c => 
            c.handle === key.split(` ${separatorSVG}`)[0].toLowerCase().replace(/ /g, '-')
          );
          if (parentCollection) {
            organizedCollections[key] = organizedCollections[key].filter(c => c !== parentCollection);
          }
        }
        
        if (organizedCollections[key].length === 0) {
          delete organizedCollections[key];
        }
      });
    
      return organizedCollections;
    }
  };

  // Hook for individual collection items
  algoliaShopify.hooks.registerHook('beforeAutocompleteCollectionsTemplate', function(_defaultTemplate, templateOptions, itemLink) {
    const { html, item, components } = templateOptions;

    // Clear old data on new search
    if (window.algoliaNewSearchStarted) {
      window.algoliaCollectionData.clear();
      window.algoliaNewSearchStarted = false;
    }

    // Store collection data if it has an image
    if (item.image) {
      window.algoliaCollectionData.set(item.handle, {
        title: item.title,
        handle: item.handle,
        image: item.image,
        url: itemLink
      });
    }

    return item?.image ? html`
      <a href="${itemLink}" class="aa-ItemLink">
        <div class="aa-ItemWrapper">
          <div class="aa-ItemContent">
            <div class="aa-ItemIcon aa-ItemIcon--noBorder custom_autocomplete_image">
              <img src="${item.image}" alt="${components.Highlight({ hit: item, attribute: 'title' })}" />    
            </div>
            <div class="aa-ItemContentTitle">
              ${components.Highlight({ hit: item, attribute: 'title' })}
            </div>
          </div>
        </div>
      </a>` : html`<img src="" class="no_image"/>`;
  });

  // Main template hook
  algoliaShopify.hooks.registerHook('beforeAutocompleteMainTemplate', function(_defaultTemplate, templateOptions, elements, displaySuggestions) {
    try {
      const { html, state } = templateOptions;
      window.algoliaNewSearchStarted = true;

      const recentSearches = utils.getFromStorage(STORAGE_KEYS.RECENT_SEARCHES);
      const recentProducts = utils.getFromStorage(STORAGE_KEYS.RECENT_PRODUCTS);
      const menuStructure = utils.getMenuStructure();
      const collections = Array.from(window.algoliaCollectionData.values());
      const organizedCollections = utils.organizeCollections(collections, menuStructure);
      const isSearchEmpty = state.query === '';

      return html`
        <div class="aa-PanelLayout aa-Panel--scrollable">
          <div class="aa-PanelSections">
            <div class="aa-PanelSection--left">
              ${displaySuggestions && html`
                <div class="aa-SourceHeader">
                  ${isSearchEmpty ? 
                    html`<span class="aa-SourceHeaderTitle suggested_collection_title" style="margin-top: 12px;">Popular Searches</span>` :
                    html`<span class="aa-SourceHeaderTitle" style="margin-top: 12px;">Suggested Searches</span>`
                  }
                </div>
                ${elements.querySuggestionsPlugin}
              `}
              ${isSearchEmpty && recentSearches.length > 0 && html`
                <div class="aa-SourceHeader">
                  <span class="aa-SourceHeaderTitle">Recent Searches</span>
                  <span class="clear_recent clear_recent_searches">Clear All</span>
                </div>
                <div class="aa-Source recent-searched-wrapper scrollable-outer-wrapper" data-autocomplete-source-id="recentsearchedwrapper">
                  <button class="scroll-arrow left-arrow-recent">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow"></path>
                      </svg>
                    </span>
                  </button>
                  <ul class="aa-List aa-Source" data-autocomplete-source-id="recentSearchesPlugin">
                    ${recentSearches.map(search => html`
                      <li class="aa-Item">
                        <div class="aa-ItemWrapper">
                          <div class="aa-ItemContent">
                            <div class="aa-ItemContentBody">
                              <a href="/search?q=${search.label}">
                                <div class="aa-ItemContentTitle">${search.label}</div>
                              </a>
                            </div>
                          </div>
                          <button class="aa-ItemDeleteButton" data-search-id="${search.id}">
                            <span>
                              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                              </svg>
                            </span>
                          </button>
                        </div>
                      </li>
                    `)}
                  </ul>
                  <button class="scroll-arrow right-arrow-recent">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow" transform="translate(100, 100) rotate(180)"></path>
                      </svg>
                    </span>
                  </button>
                </div>
              `}
            </div>
            <div class="aa-PanelSection--right">
              ${!isSearchEmpty && Object.keys(organizedCollections).length > 0 ? html`
                <div class="aa-SourceHeader ${elements.querySuggestionsPlugin ? '' : 'suggested_collection_title'}">
                  <span class="aa-SourceHeaderTitle" style="${elements.querySuggestionsPlugin ? '' : 'margin-top:8px !important'}">Suggested Collections</span>
                </div>
                <div class="suggested_collection_outer scrollable-outer-wrapper">
                  <button class="scroll-arrow left-arrow">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow"></path>
                      </svg>
                    </span>
                  </button>
                  <div class="scrollable_section">
                    ${Object.entries(organizedCollections).map(([parent, parentCollections]) => {
                      const separatorHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 9.8125L7.90625 6.40625L4.5 3" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg>';
                      
                      return html`
                        <div class="aa-CollectionGroup">
                          ${parent !== 'root' && parentCollections[0].showBreadcrumb ? html`
                            <div class="aa-CollectionParent">
                              ${parent.split(` ${separatorHTML} `).map((part, index, array) => html`
                                <a href="${parentCollections[0].fullPath[index].url}" class="aa-ItemBreadcrumbLink">
                                  <span>${html([utils.highlightMatch(part, state.query)])}</span>
                                </a>
                                ${index < array.length - 1 ? html`
                                  <span class="aa-ItemBreadcrumbSeparator">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M4.5 9.8125L7.90625 6.40625L4.5 3" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                  </span>` : ''}
                              `)}
                            </div>
                          ` : ''}
                          <ul class="aa-List">
                            ${parentCollections.map(collection => html`
                              <li class="aa-Item" data-autocomplete-source-id="collectionsPlugin">
                                <a href="${collection.url}" class="aa-ItemLink">
                                  <div class="aa-ItemWrapper">
                                    <div class="aa-ItemContent">
                                      ${collection.image ? html`
                                        <div class="aa-ItemIcon aa-ItemIcon--noBorder custom_autocomplete_image">
                                          <img src="${collection.image}" alt="${collection.title}" />    
                                        </div>
                                      ` : ''}
                                      <div class="aa-ItemContentBody">
                                        <div class="aa-ItemContentTitle">
                                          <div class="aa-ItemTitle">
                                            <span>${html([utils.highlightMatch(collection.title, state.query)])}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </a>
                              </li>
                            `)}
                          </ul>
                        </div>
                      `;
                    })}
                  </div>
                  <button class="scroll-arrow right-arrow">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow" transform="translate(100, 100) rotate(180)"></path>
                      </svg>
                    </span>
                  </button>
                </div>
              ` : ''}
              ${isSearchEmpty && recentProducts.length > 0 ? html`
                <div class="aa-SourceHeader recent-products-heading" style="${recentSearches.length === 0 ? 'margin-top:0px !important' : ''}">
                  <span class="aa-SourceHeaderTitle">Recent Products</span>
                  <span class="clear_recent clear_recent_products">Clear All</span>
                </div>
                <div class="aa-Source recent-products-wrapper scrollable-outer-wrapper" data-autocomplete-source-id="products">
                  <button class="scroll-arrow left-arrow">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow"></path>
                      </svg>
                    </span>
                  </button>
                  <ul class="aa-List home-section--content featured-collection__content featured-collection__content_custom recent-search-wrapper recent-products-container scrollable_section">
                    ${recentProducts.map(product => html`
                      <li class="aa-Item" data-product-item="" data-product-id="${product.url}" data-position="${product.position || ''}" style="--compare-height: 0px;">
                        <button class="recent-product-remove" data-primary-key="${product.url}">
                          <span>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                            </svg>
                          </span>
                        </button>  
                        <a href="${product.url}" class="aa-ItemLink aa-ProductItem">
                          <div class="card-wrapper product-card-wrapper underline-links-hover">
                                <div
                                    class="
                                        card card--card
                                        ${product.imageUrl ? 'card--media' : 'card--text'}
                                         color-scheme-0996e2a3-9bbe-49c1-8f13-f09c49aaf970 gradient
                                        card--extend-height
                                    ">
                                    <div
                                        class="card__inner  ratio" style="--ratio-percent: 100%;">
                                        ${product.imageUrl ? html`
                                            <div class="card__media">
                                                <div class="media media--transparent media--hover-effect">
                                                    <img
                                                        src="${product.imageUrl}"
                                                        alt="${product.title}"
                                                        class="motion-reduce"
                                                        loading="lazy"
                                                        style="width:100%"
                                                    />
                                                    ${product.imageUrl_alt ? html`
                                                        <img
                                                            src="${product.imageUrl_alt}"
                                                            alt="${product.title}"
                                                            class="motion-reduce"
                                                            loading="lazy"
                                                            style="width:100%"
                                                        />
                                                    ` : ''}
                                                </div>
                                            </div>
                                        ` : ''}
                                        
                                        <div class="card__content">
                                            <div class="card__information">
                                                <h3
                                                    class="card__heading"
                                                >
                                                    <a
                                                        href="${product.url}"
                                                        class="full-unstyled-link"
                                                        
                                                        title="${product.title}"
                                                    >
                                                        ${product.title}
                                                    </a>
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div class="card__content">
                                        <div class="card__information">
                                            <div class="card-information">
                                                <div class="vendor-rating-wrapper">
                                                    <!-- Vendor Display -->
                                                    ${product.vendor == 'originals' ? html`
                                                        <div class="original_brand">
                                                            <div class="original-img-wrap">
                                                                <img src="//medicalrite.com/cdn/shop/files/dealmed_1467578e-105d-4b78-b98c-ab6d8d28949b.png?v=1716913682&width=16" 
                                                                    loading="lazy" 
                                                                    class="dealmed-vendor-logo" 
                                                                    alt="Dealmed Logo" />
                                                            </div>
                                                            <div class="original_text">Original</div>
                                                        </div>
                                                    ` : html`
                                                        <span class="productitem--vendor">
                                                            <a>${product.vendor}</a>
                                                        </span>
                                                    `}
                                                    
                                                    <div class="custom_separator">|</div>
                                                    
                                                    <!-- Okendo Rating -->
                                                    ${product.reviews ? html`<div data-oke-star-rating data-oke-reviews-product-id="shopify-${product.id}" dangerouslySetInnerHTML=${createHTML(product.reviews)}></div>` : ''}
                                                </div>
                                                
                                                <h3
                                                    class="card__heading"
                                                >
                                                    <a
                                                        href="${product.url}"
                                                        class="full-unstyled-link"
                                                        
                                                        title="${product.title}"
                                                    >
                                                        ${product.title}
                                                    </a>
                                                </h3>
                                                
                                                <!-- Price Section -->
                                                <div class="price price--on-sale ">
                                                    <div class="price__container">
                                                      <div class="price__sale_new">
                                                          <span class="price__from">From</span>
                                                          <span>
                                                          ${product.currentPrice}
                                                          ${product.originalPrice && product.originalPrice !== product.currentPrice ? html`
                                                              <span class="ais-hit--price-striked">
                                                                <span>${product.originalPrice}</span>
                                                              </span>
                                                          ` : ''}
                                                          </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                          </div>
                        </a>
                      </li>
                    `)}
                  </ul>
                  <button class="scroll-arrow right-arrow">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow" transform="translate(100, 100) rotate(180)"></path>
                      </svg>
                    </span>
                  </button>
                </div>
              ` : ''}
              <div class="suggested_product_outer scrollable-product-outer-wrapper">
                ${!isSearchEmpty ? html`
                  <button class="scroll-arrow left-arrow-products">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow"></path>
                      </svg>
                    </span>
                  </button> 
                ` : ''}
                ${!isSearchEmpty && elements.products}
                ${!isSearchEmpty ? html`
                  <button class="scroll-arrow right-arrow-products">
                    <span>
                      <svg viewBox="0 0 100 100">
                        <path d="M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z" class="arrow" transform="translate(100, 100) rotate(180)"></path>
                      </svg>
                    </span>
                  </button>
                ` : ''}
              </div>
            </div>
          </div>
        </div>
      `;
    } catch (error) {
      console.error('Error in beforeAutocompleteMainTemplate:', error);
      return _defaultTemplate;
    }
  });

  // Product template hook
  algoliaShopify.hooks.registerHook("beforeAutocompleteProductTemplate", function (_defaultTemplate, templateOptions, distinct, itemLink) {
    const { html, item, components } = templateOptions;

    // Skip test products
    if (item.tags && item.tags.includes('Test Product')) {
      return null;
    }

    // Calculate ratio for aspect ratio (matching DAWN logic)
    let ratio = 1;
    if (item.product_image && '' == 'portrait') {
        ratio = 0.8;
    } else if (item.product_image && '' == 'adapt') {
        // You might need to pass aspect ratio data from your Algolia index
        ratio = item.image_aspect_ratio || 1;
    }
    if (ratio == 0 || ratio == null) {
        ratio = 1;
    }

    return html`
      <a
        href="${algoliaShopify.helpers.instantsearchLink(item)}"
        class="aa-ItemLink aa-ProductItem algolia-product-item"
        data-product-data="${encodeURIComponent(JSON.stringify({
          index: item.__autocomplete_indexName,
          queryID: item.__autocomplete_queryID,
          objectID: item.objectID,
          url: algoliaShopify.helpers.instantsearchLink(item)
        }))}"
      >
        <div class="card-wrapper product-card-wrapper underline-links-hover" data-handle="${item.handle}" data-variant-id="${item.objectID}">
          <div
              class="
                  card card--card
                  ${item.product_image ? 'card--media' : 'card--text'}
                   color-scheme-0996e2a3-9bbe-49c1-8f13-f09c49aaf970 gradient
                  
                  card--extend-height
                  ${!item.product_image && 'card' == 'card' ? 'ratio' : ''}
              "
              style="--ratio-percent: ${100 / ratio}%;"
          >
              <div
                  class="card__inner ${item.product_image || 'card' == 'standard' ? ' ratio' : ''}"
                  style="--ratio-percent: ${100 / ratio}%;"
              >
                  ${item.product_image ? html`
                      <div class="card__media">
                          <div class="media media--transparent media--hover-effect">
                              <img
                                  src="${item.product_image}"
                                  alt="${item.title}"
                                  class="motion-reduce"
                                  loading="lazy"
                                  style="width:100%"
                              />
                              ${item.product_image_alt ? html`
                                  <img
                                      src="${item.product_image_alt}"
                                      alt="${item.title}"
                                      class="motion-reduce"
                                      loading="lazy"
                                      style="width:100%"
                                  />
                              ` : ''}
                          </div>
                      </div>
                  ` : ''}
                  
                  <div class="card__content">
                      <div class="card__information">
                          <h3
                              class="card__heading"
                              ${!item.product_image && 'card' == 'standard' ? `id="title--${item.id}"` : ''}
                          >
                              <a
                                  href="/products/${item.handle}"
                                  id="StandardCardNoMediaLink--${item.id}"
                                  class="full-unstyled-link"
                                  aria-labelledby="StandardCardNoMediaLink--${item.id} NoMediaStandardBadge--${item.id}"
                                  
                                  title="${item.title}"
                              >
                                  ${item.title}
                              </a>
                          </h3>
                      </div>
                  </div>
                  
                  <!-- Swym Wishlist Plus EPI Button -->
                  <button
                      aria-label="Add to Wishlist"
                      data-with-epi="true"
                      class="swym-button swym-add-to-wishlist-view-product product_${item.id}"
                      data-swaction="addToWishlist"
                      data-product-id="${item.id}"
                      data-variant-id="${item.objectID}"
                      data-product-url="https://medicalrite.com/products/${item.handle}"
                  ></button>
              </div>
              
              <div class="card__content">
                  <div class="card__information">
                      <div class="card-information">
                          <div class="vendor-rating-wrapper">
                              <!-- Vendor Display -->
                              ${item.vendor == 'Dealmed' ? html`
                                  <div class="original_brand">
                                      <div class="original-img-wrap">
                                          <img src="//medicalrite.com/cdn/shop/files/dealmed_1467578e-105d-4b78-b98c-ab6d8d28949b.png?v=1716913682&width=16" 
                                              loading="lazy" 
                                              class="dealmed-vendor-logo" 
                                              alt="Dealmed Logo" />
                                      </div>
                                      <div class="original_text">Original</div>
                                  </div>
                              ` : html`
                                  <span class="productitem--vendor">
                                      <a>${item.vendor}</a>
                                  </span>
                              `}
                              
                              <div class="custom_separator">|</div>
                              
                              <!-- Okendo Rating -->
                              ${item.meta?.okendo?.StarRatingSnippet ? html`
                                  <div data-oke-star-rating 
                                      data-oke-reviews-product-id="shopify-${item.id}"
                                      dangerouslySetInnerHTML=${createHTML(item.meta.okendo.StarRatingSnippet)}></div>
                              ` : ''}
                          </div>
                          
                          <h3
                              class="card__heading${item.product_image || 'card' == 'standard' ? ' h5' : ''}"
                              ${item.product_image || 'card' == 'card' ? `id="title--${item.id}"` : ''}
                          >
                              <a
                                  href="/products/${item.handle}"
                                  id="CardLink--${item.id}"
                                  class="full-unstyled-link"
                                  aria-labelledby="CardLink--${item.id} Badge--${item.id}"
                                  
                                  title="${item.title}"
                              >
                                  ${item.title}
                                  ${item.options?.sold_by && item.options.sold_by !== "Single Piece" && item.options.sold_by !== "each" && item.options.sold_by !== "ea" ? 
                                      html`, <span class="title-sub-variant">${item.options.sold_by}</span>` : ''}
                              </a>
                          </h3>
                          
                          <!-- Price Section -->
                          <div class="price ${item.variants_compare_at_price_min > item.variants_min_price ? html `price--on-sale ` : ''} price--show-badge">
                              <div class="price__container">
                                  <div class="price__regular">
                                      <span class="visually-hidden visually-hidden--inline">Regular price</span>
                                      ${item.variants_count !== 1 ? html`
                                          <div class="price-item">
                                              <span class="price__from">From</span>
                                              <span class="price-item price-item--regular">
                                                  <span class="money">${algoliaShopify.helpers.displayPrice({price: item.variants_min_price}, item._distinct)}</span>
                                              </span>
                                          </div>
                                          ` : 
                                          html`
                                          <span class="price-item price-item--regular">
                                                  <span class="money">${algoliaShopify.helpers.displayPrice({price: item.variants_min_price}, item._distinct)}</span>
                                          </span>
                                      `}

                                  </div>
                                  ${item.variants_compare_at_price_min && item.variants_compare_at_price_min > item.variants_min_price ? html`
                                      <div class="price__sale">
                                          <span class="visually-hidden visually-hidden--inline">Regular price</span>
                                          <span>
                                              <s class="price-item price-item--regular">${algoliaShopify.helpers.displayStrikedPrice(item.variants_min_price, item.variants_compare_at_price_min)}</s>
                                          </span>
                                          <span class="visually-hidden visually-hidden--inline">Sale price</span>
                                          <span class="price-item price-item--sale price-item--last">
                                              ${item.variants_count !== 1 ? html`<span class="price__from">From</span>` : ''}
                                              <span class="money">${algoliaShopify.helpers.displayPrice({price : (item.variants_min_price)}, item._distinct)}</span>
                                          </span>
                                      </div>
                                  ` : ''}
                                      
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
        </div>
      </a>
    `;
  });

  // Transform response hook - converted from jQuery to vanilla JS
  algoliaShopify.hooks.registerHook('beforeAutocompleteProductTransformResponseHits', function(hits, results) {
    // Hide collections without images and show those with images
    const collectionItems = document.querySelectorAll('.aa-Source[data-autocomplete-source-id=collections] .aa-Item');
    collectionItems.forEach(item => {
      const img = item.querySelector('img');
      if (img && img.classList.contains('no_image')) {
        item.style.display = 'none';
      } else {
        item.style.display = 'flex';
      }
    });

    // Update header titles
    const collectionsTitle = document.querySelector('.aa-Source[data-autocomplete-source-id=collections] .aa-SourceHeaderTitle');
    if (collectionsTitle) {
      collectionsTitle.textContent = 'Suggested Collections';
    }

    const productsTitle = document.querySelector('.aa-Source[data-autocomplete-source-id=products] .aa-SourceHeaderTitle');
    if (productsTitle) {
      productsTitle.textContent = 'Suggested Products';
    }

    // Add "See All" link if it doesn't exist
    const productsHeader = document.querySelector('.aa-Source[data-autocomplete-source-id=products] .aa-SourceHeader');
    if (productsHeader && !productsHeader.querySelector('.see_all')) {
      const seeAllLink = document.createElement('a');
      seeAllLink.href = '';
      seeAllLink.className = 'see_all';
      seeAllLink.textContent = 'See All';
      productsTitle.insertAdjacentElement('afterend', seeAllLink);
    }

    // Update "See All" link URL
    const seeAllBtn = document.querySelector('.aa-SeeAllBtn');
    const seeAllLink = document.querySelector('.see_all');
    if (seeAllBtn && seeAllLink) {
      seeAllLink.href = seeAllBtn.href;
      seeAllBtn.style.display = 'none';
      
      seeAllLink.addEventListener('click', (e) => {
        e.preventDefault();
        seeAllBtn.click();
      });
    }

    // Handle clear button
    const clearButton = document.querySelector('.aa-ClearButton');
    const cancelButton = document.querySelector('.aa-DetachedCancelButton');
    if (clearButton && cancelButton) {
      clearButton.addEventListener('click', () => {
        cancelButton.click();
      });
    }

    return hits;
  });

  // No results template
  algoliaShopify.hooks.registerHook('beforeAutocompleteNoResultsTemplate', function(_defaultTemplate, templateOptions) {
    const { html, state } = templateOptions;
    return html`
      <div class="aa-PanelLayout aa-Panel--scrollable">
        <p class="aa-NoResultsHeader">
          ${algoliaShopify.translation_helpers.no_result_for(state.query)}
        </p>
      </div>
    `;
  });

  // Product click analytics handler
  document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('.algolia-product-item');
      if (!productLink) return;

      e.preventDefault();
      
      try {
        const productData = JSON.parse(decodeURIComponent(productLink.dataset.productData));
        
        const clickData = {
          eventName: "Product Clicked",
          index: productData.index,
          queryID: productData.queryID,
          objectIDs: [productData.objectID]
        };

        const storedData = utils.getFromStorage(STORAGE_KEYS.ANALYTICS_CLICKS);
        storedData.push(clickData);
        utils.setToStorage(STORAGE_KEYS.ANALYTICS_CLICKS, storedData);

        window.location.href = productData.url;
      } catch (err) {
        console.error('Error handling product click:', err);
        // Fallback to direct navigation
        window.location.href = productLink.href;
      }
    });
  });
});